import { api } from './api';

export interface Existencia {
  id: number;
  productoId: number;
  sucursalId: number;
  cantidad: number;
  cantidadMinima: number;
  cantidadMaxima?: number;
  costoPromedio: number;
  ubicacion?: string;
  producto?: {
    id: number;
    name: string;
    sku?: string;
    unidadMedida: string;
    manejaInventario: boolean;
  };
  sucursal?: {
    id: number;
    name: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface MovimientoInventario {
  id: number;
  productoId: number;
  sucursalId: number;
  tipo: 'entrada' | 'salida' | 'ajuste' | 'transferencia_envio' | 'transferencia_recepcion' | 'merma' | 'venta' | 'devolucion';
  cantidad: number;
  costoUnitario: number;
  motivo?: string;
  referenciaId?: number;
  usuarioId: number;
  producto?: {
    id: number;
    name: string;
    sku?: string;
  };
  sucursal?: {
    id: number;
    name: string;
  };
  usuario?: {
    id: number;
    name: string;
    email: string;
  };
  createdAt?: string;
}

export interface AjusteInventario {
  productoId: number;
  sucursalId: number;
  cantidad: number;
  tipo?: 'entrada' | 'salida' | 'ajuste' | 'merma';
  motivo?: string;
  costoUnitario?: number;
}

export interface ExistenciaUpdate {
  cantidadMinima?: number;
  cantidadMaxima?: number;
  ubicacion?: string;
}

export interface PaginatedResponse<T> {
  data: {
    [key: string]: T[];
    pagination?: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

export const inventarioService = {
  getExistencias: async (
    sucursalId?: number,
    productoId?: number,
    lowStock?: boolean
  ): Promise<Existencia[]> => {
    const params = new URLSearchParams();
    if (sucursalId) params.append('sucursalId', sucursalId.toString());
    if (productoId) params.append('productoId', productoId.toString());
    if (lowStock) params.append('lowStock', 'true');
    
    const response = await api.get<{ success: boolean; data: { existencias: Existencia[] } }>(`/inventario/existencias?${params}`);
    return response.data.data.existencias;
  },

  getExistenciaById: async (id: number): Promise<Existencia> => {
    const response = await api.get<{ success: boolean; data: { existencia: Existencia } }>(`/inventario/existencias/${id}`);
    return response.data.data.existencia;
  },

  ajustarInventario: async (data: AjusteInventario): Promise<Existencia> => {
    const response = await api.post<{ success: boolean; data: { existencia: Existencia } }>('/inventario/ajustar', data);
    return response.data.data.existencia;
  },

  updateExistencia: async (id: number, data: ExistenciaUpdate): Promise<Existencia> => {
    const response = await api.put<{ success: boolean; data: { existencia: Existencia } }>(`/inventario/existencias/${id}`, data);
    return response.data.data.existencia;
  },

  getMovimientos: async (
    page = 1,
    limit = 50,
    productoId?: number,
    sucursalId?: number,
    tipo?: string,
    fechaDesde?: string,
    fechaHasta?: string
  ): Promise<PaginatedResponse<MovimientoInventario>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    if (productoId) params.append('productoId', productoId.toString());
    if (sucursalId) params.append('sucursalId', sucursalId.toString());
    if (tipo) params.append('tipo', tipo);
    if (fechaDesde) params.append('fechaDesde', fechaDesde);
    if (fechaHasta) params.append('fechaHasta', fechaHasta);
    
    const response = await api.get<PaginatedResponse<MovimientoInventario>>(`/inventario/movimientos?${params}`);
    return response.data;
  }
};

